/**
 * React Query hooks for User Profile
 * 
 * Handles profile fetching and updates for the current user.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi, type ProfileUpdateRequest, type User } from '../lib/api/profile';
import { usersApi } from '../lib/api/users';
import { useOroMutation, OroOperation } from './useOroMutation';

/**
 * Fetch current user's profile
 * 
 * Query Key: ['profile']
 * Returns: User profile data
 */
export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await profileApi.getProfile();
      return response.profile;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - profile doesn't change frequently
  });
}

/**
 * Update current user's profile
 * 
 * On success: Invalidates ['profile'] and ['me'] to refresh data
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  
  return useOroMutation<User, ProfileUpdateRequest>({
    mutationFn: async (data: ProfileUpdateRequest) => {
      const response = await profileApi.updateProfile(data);
      return response.profile;
    },
    onSuccess: (updatedProfile) => {
      // #3075: cancel BEFORE writing, matching useVerifyEmail (#2989). A
      // `['profile']` GET that started before this mutation can settle AFTER the
      // setQueryData below and overwrite the fresh row with stale data — the
      // standard TanStack optimistic-write hazard cancelQueries closes. `void`
      // not `await`: query.cancel() runs synchronously inside cancelQueries'
      // notifyManager.batch before the returned promise settles, and
      // useOroMutation does not await this callback (see useVerifyEmail).
      void queryClient.cancelQueries({ queryKey: ['profile'] });
      // Update the profile cache
      queryClient.setQueryData(['profile'], updatedProfile);
      // Invalidate and refetch 'me' immediately to update top nav
      queryClient.invalidateQueries({
        queryKey: ['me'],
        refetchType: 'active' // Force immediate refetch of active queries
      });
    },
    successToast: 'Profile updated',
    oro: {
      op: OroOperation.UpdateProfile,
      retryPolicy: 'never',
      debugLabel: 'Profile edit form',
    },
  });
}

/**
 * Remove profile image
 * 
 * On success: Invalidates ['profile'] to refresh data
 */
export function useRemoveProfileImage() {
  const queryClient = useQueryClient();

  return useOroMutation<{ ok: true }, void>({
    mutationFn: profileApi.removeProfileImage,
    onSuccess: () => {
      // Invalidate profile to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // Force immediate refetch of 'me' to update top nav
      queryClient.invalidateQueries({ 
        queryKey: ['me'],
        refetchType: 'active'
      });
    },
    oro: {
      op: OroOperation.RemoveProfileImage,
      retryPolicy: 'never',
      debugLabel: 'Profile image removal',
    },
  });
}

/**
 * Request phone verification code (#1461)
 */
export function useRequestPhoneVerification() {
  return useOroMutation<{ success: boolean; message: string }, string>({
    mutationFn: (phone: string) => profileApi.requestPhoneVerification(phone),
    successToast: 'Verification code sent',
    oro: {
      op: OroOperation.RequestPhoneVerification,
      retryPolicy: 'never',
      debugLabel: 'Phone verification request',
    },
  });
}

/**
 * Verify phone with code (#1461)
 */
export function useVerifyPhone() {
  const queryClient = useQueryClient();

  return useOroMutation<{ profile: User }, string>({
    mutationFn: (code: string) => profileApi.verifyPhone(code),
    onSuccess: (data) => {
      // #3075: cancel BEFORE writing (see useVerifyEmail / #2989) so an
      // in-flight `['profile']` GET cannot settle afterwards and revert the
      // verified badge with a stale row.
      void queryClient.cancelQueries({ queryKey: ['profile'] });
      queryClient.setQueryData(['profile'], data.profile);
      queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'active' });
    },
    successToast: 'Phone number verified',
    oro: {
      op: OroOperation.VerifyPhone,
      retryPolicy: 'never',
      debugLabel: 'Phone verification confirm',
    },
  });
}

/**
 * Remove phone number (#1461)
 */
export function useRemovePhone() {
  const queryClient = useQueryClient();

  return useOroMutation<{ profile: User }, void>({
    mutationFn: () => profileApi.removePhone(),
    onSuccess: (data) => {
      // #3075: cancel BEFORE writing (see useVerifyEmail / #2989) so an
      // in-flight `['profile']` GET cannot settle afterwards and revert this
      // write with a stale row.
      void queryClient.cancelQueries({ queryKey: ['profile'] });
      queryClient.setQueryData(['profile'], data.profile);
      queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'active' });
    },
    successToast: 'Phone number removed',
    oro: {
      op: OroOperation.RemovePhone,
      retryPolicy: 'never',
      debugLabel: 'Phone removal',
    },
  });
}

/**
 * Request an add-email verification code (#2930) — POST /profile/email/request.
 *
 * Phone-only STOP-lockout recovery: sends a 6-digit code to a candidate real
 * email so a synthetic `{phone}@sms.oro.fit` account can add its first real
 * address. No success toast — the section flips to its inline "enter the code"
 * step, which is the confirmation. Errors surface inline at the call site
 * (distinct backend copy per branch: real-email-already, synthetic candidate,
 * taken, throttled), so `retryPolicy: 'never'`.
 */
export function useRequestEmailVerification() {
  return useOroMutation<{ success: boolean; message: string }, string>({
    mutationFn: (email: string) => profileApi.requestEmailVerification(email),
    oro: {
      op: OroOperation.RequestEmailVerification,
      retryPolicy: 'never',
      // #2989: AddEmailSection renders the backend's exact per-branch copy
      // inline (real-email-already / synthetic candidate / taken / throttled).
      // Suppress the wrapper's generic toast so the two don't double-present.
      suppressErrorToast: true,
      debugLabel: 'Add-email verification request',
    },
  });
}

/**
 * Verify the add-email code and commit the new email (#2930) — POST
 * /profile/email/verify. On success the returned profile carries the real
 * address (no longer synthetic), so the Account section re-renders as the normal
 * change-email view. Session is deliberately preserved (owner decision on
 * #2930): it is the locked-out user's only recovery window.
 *
 * ACCEPTED THREAT, written down on purpose (George round 3, Q2) — do NOT
 * "fix" this by adding a `session_version` bump. Because the session survives,
 * someone holding a STOLEN live session can attach an attacker-controlled inbox
 * here and keep the account after the phone path is dead: a durable takeover.
 * That is the deliberate trade — a bump would revoke the very session this flow
 * exists to rescue, slamming the door on the legitimate locked-out user, who by
 * definition has no other way in. The backend skips the bump for the same
 * reason (`routes/profile.ts`, the `/email/verify` commit). Rule 26 still
 * governs the #1335 change-a-REAL-email path, which does revoke all sessions.
 */
export function useVerifyEmail() {
  const queryClient = useQueryClient();

  return useOroMutation<{ profile: User }, string>({
    mutationFn: (code: string) => profileApi.verifyEmail(code),
    onSuccess: (data) => {
      // Cancel BEFORE writing (George/Frank/cloud-Codex convergent). A
      // `['profile']` GET that started before the commit settles afterwards and
      // overwrites this write with the OLD synthetic address — the standard
      // TanStack optimistic-write hazard, which is why their recipe opens with
      // cancelQueries. The server state is fine; the UI is not, and the failure
      // is nasty precisely here: the section flips back to add-email, a retry
      // hits the backend's "account already has an email, use change-email"
      // branch, and Change Email is hidden because the cache still reads
      // synthetic. A locked-out user is dead-ended until they hard-refresh.
      //
      // `void`, not `await`: `query.cancel()` runs synchronously inside
      // cancelQueries' notifyManager.batch (query-core queryClient.js) before
      // the returned promise settles, so the cancellation is already in effect
      // — and awaiting would push the write into a later microtask, since
      // useOroMutation calls this callback without awaiting it.
      void queryClient.cancelQueries({ queryKey: ['profile'] });
      queryClient.setQueryData(['profile'], data.profile);
      queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'active' });
    },
    successToast: 'Email address added',
    oro: {
      op: OroOperation.VerifyEmail,
      retryPolicy: 'never',
      // #2989: AddEmailSection renders the verify-step error inline (bad code /
      // expired / taken). Suppress the wrapper toast so it doesn't fire the
      // generic copy alongside the precise inline message.
      suppressErrorToast: true,
      debugLabel: 'Add-email verification confirm',
    },
  });
}

/**
 * Fetch public profile by user ID
 * 
 * Query Key: ['publicProfile', userId]
 * Returns: Public user profile data
 */
export function usePublicProfile(userId: string, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ['publicProfile', userId],
    queryFn: async () => {
      const response = await usersApi.getPublicProfile(userId);
      return response.profile;
    },
    // #3370: `enabled` is REQUIRED, not defaulted. The endpoint requires auth,
    // so fetching as a guest caches a 401 into this key (5-min staleTime) that
    // login cannot clear (see PublicProfilePage). A defaulted-true option let a
    // future caller silently reintroduce that bug; forcing the caller to state
    // the auth gate keeps the decision at the call site.
    enabled: !!userId && options.enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes - public profiles don't change frequently
  });
}
